import { Stack } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { FieldValues, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { getDepartmentByFacultyOptions } from '@/module/Department/model/DepartmentQuery';
import { getDisciplineByDepartmentOptions } from '@/module/Discipline/model/DisciplineQuery';
import { getSuspenseFacultyOptions } from '@/module/Faculty/model/FacultyQuery';

import { AsyncSelectControl, AsyncSelectControlProps } from '@/core/components';

type FacultyDepartmentSelectControlProps<T extends FieldValues> = Omit<
  AsyncSelectControlProps<T>,
  'isError' | 'isLoading' | 'options' | 'name' | 'label'
>;

export function FacultyDepartmentSelectControl<T extends FieldValues>({
  control,
  ...props
}: FacultyDepartmentSelectControlProps<T>) {
  const { setValue } = useFormContext();
  const { data, isLoading, isError } = useQuery(getSuspenseFacultyOptions());
  const { t } = useTranslation();

  // @ts-expect-error fix later
  const faculty = useWatch({ control, name: 'faculty' });
  const facultyId = faculty?.id;

  // @ts-expect-error fix later
  const department = useWatch({ control, name: 'department' });
  const departmentId = department?.id;

  useEffect(() => {
    setValue('department', null);
    setValue('discipline', null);
  }, [facultyId, setValue]);

  useEffect(() => {
    setValue('discipline', null);
  }, [departmentId, setValue]);

  const selectedFaculty = (data ?? []).find(f => f.id === facultyId);

  const { data: availableDepartments = [] } = useQuery({
    ...getDepartmentByFacultyOptions(selectedFaculty?.id ?? 0),
    enabled: !!selectedFaculty,
  });

  const selectedDepartment = availableDepartments.find(d => d.id === departmentId);

  const { data: availableDisciplines = [] } = useQuery({
    ...getDisciplineByDepartmentOptions(selectedDepartment?.id ?? 0),
    enabled: !!selectedDepartment,
  });

  return (
    <Stack>
      {/* @ts-expect-error fix later */}
      <AsyncSelectControl
        isError={isError}
        isLoading={isLoading}
        options={data ?? []}
        name="faculty"
        control={control}
        label={t('setup.fields.faculty')}
        {...props}
      />
      {/* @ts-expect-error fix later */}
      <AsyncSelectControl
        isError={isError}
        isLoading={isLoading}
        isDisabled={facultyId == null}
        options={availableDepartments}
        control={control}
        name="department"
        label={t('setup.fields.department')}
        {...props}
      />
      {/* @ts-expect-error fix later */}
      <AsyncSelectControl
        isError={isError}
        isLoading={isLoading}
        isDisabled={departmentId == null}
        options={availableDisciplines}
        control={control}
        name="discipline"
        label={t('setup.fields.discipline')}
        {...props}
      />
    </Stack>
  );
}
